import React, { useState, useEffect, useContext, useRef } from 'react';
import AppContext from '../../contexts/AppContext';
import { MapPin, Calendar, Trophy, X, Share2, Send, Users, CheckCircle, Plus, Lock, Edit, Trash2 } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import SecurityUtils from '../../utils/security';
import { formatTime12hr, formatEventDate, parseEventDate } from '../../utils/formatting';
import DateInput from '../common/DateInput';
import { haversineDistance } from '../../utils/location';
import Avatar from '../../components/common/Avatar';
import GooglePlacesAutocomplete from '../../components/common/GooglePlacesAutocomplete';
import GameSetupModal from '../../components/events/GameSetupModal';

const EventsView = () => {
  const { events, setEvents, users, currentUser, viewUserProfile, conversations, setConversations, setView, groups, setActiveGame, setSelectedGameId, showAlert, showConfirm } = useContext(AppContext);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [eventTab, setEventTab] = useState('active');
  const [editingEvent, setEditingEvent] = useState(null);
  const [showPlayerList, setShowPlayerList] = useState(null);
  const [showShareModal, setShowShareModal] = useState(null); // event ID to share
  const [showInviteEvent, setShowInviteEvent] = useState(null); // event ID for invite modal
  const [showGameSetup, setShowGameSetup] = useState(null); // event object for game setup
  const [inviteTab, setInviteTab] = useState('users');
  const [selectedInviteUsers, setSelectedInviteUsers] = useState([]);
  const [selectedInviteGroups, setSelectedInviteGroups] = useState([]);
  const [eventGames, setEventGames] = useState({}); // { eventId: gameData }
  const [courseResults, setCourseResults] = useState([]);
  const [courseSearching, setCourseSearching] = useState(false);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const courseSearchTimeout = useRef(null);

  const handleCourseSearch = (query) => {
    setNewEvent(prev => ({ ...prev, course: query }));
    setShowCourseDropdown(true);
    if (query.trim().length < 2) { setCourseResults([]); return; }
    clearTimeout(courseSearchTimeout.current);
    courseSearchTimeout.current = setTimeout(async () => {
      setCourseSearching(true);
      try {
        const result = await firestoreService.searchCourses({
          query: query.trim(),
          lat: currentUser.location?.lat || null,
          lng: currentUser.location?.lon || null,
        });
        setCourseResults(result.courses || []);
      } catch (err) {
        setCourseResults([]);
      }
      setCourseSearching(false);
    }, 400);
  };

  const handleCourseSelect = (course) => {
    const name = course.clubName || course.courseName;
    setNewEvent(prev => ({ ...prev, course: name }));
    setCourseResults([]);
    setShowCourseDropdown(false);
  };

  // Load games for all events
  useEffect(() => {
    const loadGames = async () => {
      const gamesMap = {};
      for (const event of events) {
        try {
          const games = await firestoreService.getEventGames(event.id);
          if (games.length > 0) {
            // Get the most recent game
            const latest = games.sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''))[0];
            gamesMap[event.id] = latest;
          }
        } catch (err) { /* ignore */ }
      }
      setEventGames(gamesMap);
    };
    if (events.length > 0) loadGames();
  }, [events]);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    course: '',
    address: '',
    addressLat: null,
    addressLon: null,
    maxPlayers: 4,
    visibility: 'public'
  });

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date || !newEvent.time || !newEvent.course || !newEvent.address) return;

    try {
      const eventData = {
        userId: currentUser.id,
        title: SecurityUtils.sanitizeText(newEvent.title),
        description: SecurityUtils.sanitizeText(newEvent.description || ''),
        date: newEvent.date,
        time: newEvent.time,
        course: SecurityUtils.sanitizeText(newEvent.course),
        address: SecurityUtils.sanitizeText(newEvent.address),
        addressLat: newEvent.addressLat || null,
        addressLon: newEvent.addressLon || null,
        maxPlayers: newEvent.maxPlayers,
        attendees: [currentUser.id],
        visibility: newEvent.visibility,
      };

      const createdEvent = await firestoreService.createEvent(eventData);

      // Create group chat for the event and store its ID on the event
      const eventDateFormatted = formatEventDate(newEvent.date);
      const groupChat = await firestoreService.createGroupChat(createdEvent.id, newEvent.title, eventDateFormatted, currentUser.id, []);
      await firestoreService.updateEvent(createdEvent.id, { groupChatId: groupChat.id });

      setShowCreateModal(false);
      setNewEvent({ title: '', description: '', date: '', time: '', course: '', address: '', addressLat: null, addressLon: null, maxPlayers: 4, visibility: 'public' });
    } catch (err) {
      console.error('Error creating event:', err);
      showAlert('Failed to create event. Please try again.', 'Error');
    }
  };

  const handleJoinLeaveEvent = async (eventId) => {
    const targetEvent = events.find(e => e.id === eventId);
    if (!targetEvent) return;

    const isAttending = targetEvent.attendees.includes(currentUser.id);

    // Event owners cannot leave their own events
    if (isAttending && targetEvent.userId === currentUser.id) return;

    try {
      if (isAttending) {
        await firestoreService.leaveEvent(eventId, currentUser.id);
        // Remove from group chat using stored groupChatId
        if (targetEvent.groupChatId) {
          await firestoreService.removeParticipant(targetEvent.groupChatId, currentUser.id);
        }
      } else {
        if (targetEvent.attendees.length >= targetEvent.maxPlayers) return;
        await firestoreService.joinEvent(eventId, currentUser.id);
        // Add to group chat using stored groupChatId, or create one
        if (targetEvent.groupChatId) {
          await firestoreService.addParticipant(targetEvent.groupChatId, currentUser.id);
        } else {
          const eventDate = formatEventDate(targetEvent.date);
          const groupChat = await firestoreService.createGroupChat(eventId, targetEvent.title, eventDate, targetEvent.userId, [...targetEvent.attendees, currentUser.id]);
          await firestoreService.updateEvent(eventId, { groupChatId: groupChat.id });
        }
        // Notify event creator about join
        if (targetEvent.userId !== currentUser.id) {
          firestoreService.createNotification({
            targetUserId: targetEvent.userId,
            fromUserId: currentUser.id,
            type: 'event_join',
            message: `joined your event "${targetEvent.title}"`,
          }).catch(err => console.error('Error creating event join notification:', err));
        }
      }
    } catch (err) {
      console.error('Error joining/leaving event:', err);
    }

    // The rest is handled by real-time listeners, but let's also do optimistic local update
    setEvents(prev => prev.map(event => {
      if (event.id === eventId) {
        if (isAttending) {
          return { ...event, attendees: event.attendees.filter(id => id !== currentUser.id) };
        } else if (event.attendees.length < event.maxPlayers) {
          return { ...event, attendees: [...event.attendees, currentUser.id] };
        }
      }
      return event;
    }));
  };

  const handleEditEvent = (event) => {
    // Convert ISO date to YYYY-MM-DD format for date input
    const formattedDate = event.date.includes('T') ? event.date.split('T')[0] : event.date;

    setEditingEvent(event.id);
    setNewEvent({
      title: event.title,
      description: event.description,
      date: formattedDate,
      time: event.time,
      course: event.course,
      address: event.address,
      addressLat: event.addressLat || null,
      addressLon: event.addressLon || null,
      maxPlayers: event.maxPlayers,
      visibility: event.visibility || 'public'
    });
  };

  const handleSaveEdit = async () => {
    if (!newEvent.title || !newEvent.date || !newEvent.time || !newEvent.course || !newEvent.address) return;

    const updatedData = {
      title: SecurityUtils.sanitizeText(newEvent.title),
      description: SecurityUtils.sanitizeText(newEvent.description || ''),
      date: newEvent.date,
      time: newEvent.time,
      course: SecurityUtils.sanitizeText(newEvent.course),
      address: SecurityUtils.sanitizeText(newEvent.address),
      addressLat: newEvent.addressLat || null,
      addressLon: newEvent.addressLon || null,
      maxPlayers: newEvent.maxPlayers,
      visibility: newEvent.visibility,
      edited: true,
      editedAt: new Date().toISOString()
    };

    setEvents(events.map(event => event.id === editingEvent ? { ...event, ...updatedData } : event));
    setEditingEvent(null);
    setNewEvent({ title: '', description: '', date: '', time: '', course: '', address: '', addressLat: null, addressLon: null, maxPlayers: 4, visibility: 'public' });

    try { await firestoreService.updateEvent(editingEvent, updatedData); } catch (err) { console.error('Error updating event:', err); }
  };

  const handleCancelEdit = () => {
    setEditingEvent(null);
    setNewEvent({
      title: '',
      description: '',
      date: '',
      time: '',
      course: '',
      address: '',
      maxPlayers: 4,
      visibility: 'public'
    });
  };

  // Get mutuals (both follow each other)
  const mutuals = users.filter(u =>
    u.id !== currentUser.id &&
    currentUser.following?.includes(u.id) &&
    u.following?.includes(currentUser.id)
  );

  const handleShareEvent = async (targetUserId, event) => {
    try {
      // Find or create DM conversation
      let conv = conversations.find(c =>
        !c.isGroup &&
        c.participants?.includes(currentUser.id) &&
        c.participants?.includes(targetUserId) &&
        c.participants?.length === 2
      );
      if (!conv) {
        const allConvs = await firestoreService.getConversations(currentUser.id);
        conv = allConvs.find(c =>
          !c.isGroup &&
          c.participants?.includes(targetUserId) &&
          c.participants?.length === 2
        );
      }
      if (!conv) {
        conv = await firestoreService.createConversation({
          participants: [currentUser.id, targetUserId],
          lastMessage: '',
          lastMessageTime: new Date().toISOString(),
        });
        setConversations(prev => [...prev, conv]);
      }

      const eventDate = formatEventDate(event.date);
      await firestoreService.sendMessage({
        conversationId: conv.id,
        senderId: currentUser.id,
        content: `Check out this event! "${event.title}" on ${eventDate} at ${event.course}`,
        sharedEventId: event.id,
        sharedEventTitle: event.title,
        sharedEventDate: eventDate,
        sharedEventCourse: event.course || '',
      });

      setShowShareModal(null);
      // Notify recipient about shared event
      firestoreService.createNotification({
        targetUserId,
        fromUserId: currentUser.id,
        type: 'share',
        message: `shared an event with you: "${event.title}"`,
      }).catch(err => console.error('Error creating event share notification:', err));
    } catch (err) {
      console.error('Error sharing event:', err);
    }
  };

  const handleInviteToEvent = async (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    try {
      // Invite individual users
      if (selectedInviteUsers.length > 0) {
        await firestoreService.inviteUsersToEvent(eventId, selectedInviteUsers);
        for (const userId of selectedInviteUsers) {
          firestoreService.createNotification({
            targetUserId: userId,
            fromUserId: currentUser.id,
            type: 'eventInvite',
            message: `invited you to "${event.title}"`,
            eventId,
          }).catch(err => console.error('Error creating invite notification:', err));
        }
      }
      // Invite groups
      for (const groupId of selectedInviteGroups) {
        await firestoreService.inviteGroupToEvent(eventId, groupId);
        const group = groups.find(g => g.id === groupId);
        if (group) {
          for (const memberId of group.members) {
            if (memberId !== currentUser.id && !event.attendees?.includes(memberId)) {
              firestoreService.createNotification({
                targetUserId: memberId,
                fromUserId: currentUser.id,
                type: 'eventInvite',
                message: `invited your group "${group.name}" to "${event.title}"`,
                eventId,
              }).catch(err => console.error('Error creating group invite notification:', err));
            }
          }
        }
      }
      // Optimistic update
      setEvents(prev => prev.map(e => e.id === eventId ? {
        ...e,
        invitedUsers: [...(e.invitedUsers || []), ...selectedInviteUsers],
        invitedGroups: [...(e.invitedGroups || []), ...selectedInviteGroups],
      } : e));
      setShowInviteEvent(null);
      setSelectedInviteUsers([]);
      setSelectedInviteGroups([]);
    } catch (err) {
      console.error('Error inviting to event:', err);
    }
  };

  const handleAcceptInvite = async (eventId) => {
    try {
      const event = events.find(e => e.id === eventId);
      if (!event || event.attendees?.length >= event.maxPlayers) return;
      await firestoreService.joinEvent(eventId, currentUser.id);
      await firestoreService.removeEventInvite(eventId, currentUser.id);
      if (event.groupChatId) {
        await firestoreService.addParticipant(event.groupChatId, currentUser.id);
      }
      if (event.userId !== currentUser.id) {
        firestoreService.createNotification({
          targetUserId: event.userId,
          fromUserId: currentUser.id,
          type: 'event_join',
          message: `accepted your invite and joined "${event.title}"`,
        }).catch(err => console.error('Error creating join notification:', err));
      }
    } catch (err) {
      console.error('Error accepting invite:', err);
    }
  };

  const handleDeclineInvite = async (eventId) => {
    try {
      await firestoreService.removeEventInvite(eventId, currentUser.id);
    } catch (err) {
      console.error('Error declining invite:', err);
    }
  };

  // Check if user is invited (directly or via group)
  const isUserInvited = (event) => {
    if (event.invitedUsers?.includes(currentUser.id)) return true;
    if (event.invitedGroups?.length > 0) {
      return event.invitedGroups.some(gId => {
        const g = groups.find(gr => gr.id === gId);
        return g?.members?.includes(currentUser.id);
      });
    }
    return false;
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-white">Golf Events</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]"
        >
          <Plus className="w-5 h-5" />
          <span>Create</span>
        </button>
      </div>

      <div className="bg-black rounded-lg shadow border border-gray-700 mb-4">
        <div className="flex border-b border-gray-800">
          <button
            onClick={() => setEventTab('active')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              eventTab === 'active' ? 'text-white border-b-2 border-white font-bold' : 'text-white hover:text-white'
            }`}
          >
            Active Golfing Events
          </button>
          <button
            onClick={() => setEventTab('past')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              eventTab === 'past' ? 'text-white border-b-2 border-white font-bold' : 'text-white hover:text-white'
            }`}
          >
            Past Golfing Events
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {(() => {
          const filteredEvents = [...events].filter(event => {
            const isPast = event.status === 'ended' || parseEventDate(event.date) < new Date();
            if (eventTab === 'active' && isPast) return false;
            if (eventTab === 'past' && !isPast) return false;
          if (event.visibility !== 'private') return true;
          return event.userId === currentUser.id ||
                 event.attendees?.includes(currentUser.id) ||
                 isUserInvited(event);
        });
          if (filteredEvents.length === 0) {
            return (
              <div className="text-center py-12">
                <p className="text-gray-400">{eventTab === 'active' ? 'No active events found.' : 'No past events found.'}</p>
              </div>
            );
          }
          return filteredEvents.map(event => {
          const cLoc = currentUser.location;
          let distance = null;
          if (event.addressLat && event.addressLon && cLoc?.lat && cLoc?.lon) {
            distance = haversineDistance(cLoc.lat, cLoc.lon, event.addressLat, event.addressLon);
          }
          return { ...event, _distance: distance };
        }).sort((a, b) => {
          if (a._distance === null && b._distance === null) return 0;
          if (a._distance === null) return 1;
          if (b._distance === null) return -1;
          return a._distance - b._distance;
        }).map(event => {
          const organizer = users.find(u => u.id === event.userId);
          return (
            <div id={`event-${event.id}`} key={event.id} className="bg-black rounded-lg shadow border border-gray-700 p-6">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white cursor-pointer hover:text-[#16a34a] transition-colors flex items-center" onClick={() => setView('event-detail-' + event.id)}>
                    {event.title}
                    {event.visibility === 'private' && (
                      <span className="ml-2 inline-flex items-center space-x-1 px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded-full font-medium">
                        <Lock className="w-3 h-3" />
                        <span>Private</span>
                      </span>
                    )}
                  </h3>
                  {event.edited && (
                    <p className="text-xs text-white italic">Edited</p>
                  )}
                </div>
                {event.userId === currentUser.id && (
                  <div className="flex items-center space-x-1">
                    {parseEventDate(event.date) > new Date() && (
                      <button
                        onClick={() => handleEditEvent(event)}
                        className="p-2 text-white hover:text-[#16a34a] hover:bg-gray-800 rounded-lg"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!await showConfirm('Are you sure you want to delete this event? This cannot be undone.', 'Delete Event', 'Delete', true)) return;
                        setEvents(events.filter(e => e.id !== event.id));
                        try {
                          // Send system message and disable group chat
                          if (event.groupChatId) {
                            await firestoreService.sendMessage({
                              conversationId: event.groupChatId,
                              senderId: 'system',
                              content: 'Event was deleted by the owner.',
                            });
                            await firestoreService.updateConversation(event.groupChatId, { disabled: true });
                          }
                          await firestoreService.deleteEvent(event.id);
                        } catch (err) {
                          console.error('Error deleting event:', err);
                        }
                      }}
                      className="p-2 text-white hover:text-red-500 hover:bg-gray-800 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-sm text-white mb-3">By {organizer?.name} (@{organizer?.handle})</p>
              <p className="text-white mb-4">{event.description}</p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm">
                  <Calendar className="w-4 h-4 text-white" />
                  <span className="text-white">{formatEventDate(event.date)} at {formatTime12hr(event.time)}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <MapPin className="w-4 h-4 text-white" />
                  <div>
                    <div className="text-white">{event.course}{event._distance != null && <span className="ml-2 text-xs text-gray-400">({Math.round(event._distance)} mi away)</span>}</div>
                    {event.address && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white font-bold hover:text-[#16a34a]"
                      >
                        {event.address}
                      </a>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowPlayerList(event.id)}
                  className="flex items-center space-x-2 text-sm text-white hover:text-[#16a34a] transition-colors"
                >
                  <Users className="w-4 h-4" />
                  <span className="font-bold">{event.attendees.length} / {event.maxPlayers} players</span>
                </button>
              </div>
              {/* Invited badge — only for future events */}
              {isUserInvited(event) && !event.attendees?.includes(currentUser.id) && event.status !== 'ended' && parseEventDate(event.date) >= new Date() && (
                <div className="flex items-center space-x-2 mb-3">
                  <span className="px-2 py-1 bg-yellow-600 text-pure-white text-xs rounded-full font-bold">Invited</span>
                  <button onClick={() => handleAcceptInvite(event.id)} className="px-2 py-1 bg-[#16a34a] text-pure-white text-xs rounded-lg hover:bg-[#15803d]">Accept</button>
                  <button onClick={() => handleDeclineInvite(event.id)} className="px-2 py-1 bg-gray-700 text-white text-xs rounded-lg hover:bg-gray-600">Decline</button>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {event.status === 'ended' || parseEventDate(event.date) < new Date() ? (
                  <p className="flex-1 px-2 py-1 rounded-lg bg-gray-700 text-gray-400 text-center">{event.status === 'ended' ? 'Event has ended' : 'Event has passed'}</p>
                ) : event.userId === currentUser.id ? (
                  <p className="flex-1 px-2 py-1 rounded-lg bg-gray-800 text-gray-400 text-center text-sm">You are the event owner</p>
                ) : event.visibility === 'private' && !event.attendees?.includes(currentUser.id) ? (
                  <p className="flex-1 px-2 py-1 rounded-lg bg-gray-800 text-gray-400 text-center text-sm flex items-center justify-center space-x-1">
                    <Lock className="w-4 h-4" />
                    <span>Private Event — Invite Only</span>
                  </p>
                ) : (
                  <button
                    onClick={() => handleJoinLeaveEvent(event.id)}
                    className={`flex-1 px-2 py-1 rounded-lg ${
                      event.attendees.includes(currentUser.id)
                        ? 'bg-red-600 text-pure-white hover:bg-red-700'
                        : event.attendees.length >= event.maxPlayers
                          ? 'bg-gray-700 text-white cursor-not-allowed'
                          : 'bg-[#16a34a] text-pure-white hover:bg-[#15803d]'
                    }`}
                    disabled={!event.attendees.includes(currentUser.id) && event.attendees.length >= event.maxPlayers}
                  >
                    {event.attendees.includes(currentUser.id) ? 'Leave Event' :
                     event.attendees.length >= event.maxPlayers ? 'Event Full' : 'Join Event'}
                  </button>
                )}
                {event.userId === currentUser.id && event.status !== 'ended' && parseEventDate(event.date) >= new Date() && (
                  <button onClick={() => setShowInviteEvent(event.id)} className="px-2 py-1 bg-blue-600 text-pure-white rounded-lg hover:bg-blue-700 flex items-center space-x-1">
                    <Send className="w-4 h-4" />
                    <span>Invite</span>
                  </button>
                )}
                {event.attendees?.includes(currentUser.id) && event.attendees.length >= 2 && (() => {
                  const eventGame = eventGames[event.id];
                  const isEventOwner = event.userId === currentUser.id;
                  const eventDateObj = parseEventDate(event.date);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isBeforeEventDate = eventDateObj < today ? false : eventDateObj.toDateString() === today.toDateString() ? false : true;

                  if (eventGame?.status === 'completed') {
                    return (
                      <button onClick={async () => {
                        try {
                          const g = await firestoreService.getGame(eventGame.id);
                          if (g) { setActiveGame(g); setSelectedGameId(g.id); setView('game-recap'); }
                        } catch (err) { console.error('Error loading game:', err); }
                      }} className="px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] flex items-center space-x-1">
                        <Trophy className="w-4 h-4" />
                        <span>View Stats</span>
                      </button>
                    );
                  }
                  if (eventGame?.status === 'active') {
                    return (
                      <button onClick={async () => {
                        try {
                          const g = await firestoreService.getGame(eventGame.id);
                          if (g) { setActiveGame(g); setView('live-score'); }
                        } catch (err) { console.error('Error loading game:', err); }
                      }} className="px-2 py-1 bg-purple-600 text-pure-white rounded-lg hover:bg-purple-700 flex items-center space-x-1">
                        <Trophy className="w-4 h-4" />
                        <span>See Game</span>
                      </button>
                    );
                  }
                  if (isEventOwner) {
                    return (
                      <button onClick={() => setShowGameSetup(event)} disabled={isBeforeEventDate} className={`px-2 py-1 rounded-lg flex items-center space-x-1 ${isBeforeEventDate ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-[#16a34a] text-pure-white hover:bg-[#15803d]'}`}>
                        <Trophy className="w-4 h-4" />
                        <span>Start Game</span>
                      </button>
                    );
                  }
                  return null;
                })()}
                {mutuals.length > 0 && event.visibility !== 'private' && (
                  <button
                    onClick={() => setShowShareModal(event.id)}
                    className="px-2 py-1 bg-gray-700 text-white rounded-lg hover:bg-gray-600 flex items-center space-x-1"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Share</span>
                  </button>
                )}
              </div>
            </div>
          );
        });
        })()}
      </div>

      {showPlayerList && (() => {
        const event = events.find(e => e.id === showPlayerList);
        if (!event) return null;

        const attendeeUsers = event.attendees
          .map(userId => users.find(u => u.id === userId))
          .filter(u => u)
          .sort((a, b) => {
            const firstNameA = a.name.split(' ')[0].toLowerCase();
            const firstNameB = b.name.split(' ')[0].toLowerCase();
            return firstNameA.localeCompare(firstNameB);
          });

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-black border border-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Event Attendees ({attendeeUsers.length})</h3>
                <button onClick={() => setShowPlayerList(null)} className="text-white hover:text-gray-300">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-3">
                {attendeeUsers.length === 0 ? (
                  <p className="text-white text-center py-4">No attendees yet</p>
                ) : (
                  attendeeUsers.map(user => (
                    <button
                      key={user.id}
                      onClick={() => { setShowPlayerList(null); viewUserProfile(user.id); }}
                      className="flex items-center space-x-3 p-2 hover:bg-gray-900 rounded-lg transition-colors w-full text-left"
                    >
                      <Avatar user={user} size="md" />
                      <div>
                        <p className="text-white font-bold">{user.name}</p>
                        <p className="text-sm text-gray-400">@{user.handle}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {showShareModal && (() => {
        const event = events.find(e => e.id === showShareModal);
        if (!event) return null;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-black border border-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Share Event</h3>
                <button onClick={() => setShowShareModal(null)} className="text-white hover:text-gray-300">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-400 mb-3">Share "{event.title}" with a mutual</p>
              <div className="space-y-2">
                {mutuals.map(u => (
                  <button
                    key={u.id}
                    onClick={() => handleShareEvent(u.id, event)}
                    className="flex items-center justify-between w-full p-2 hover:bg-gray-900 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <Avatar user={u} size="md" />
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">{u.name}</p>
                        <p className="text-xs text-gray-400">@{u.handle}</p>
                      </div>
                    </div>
                    <Send className="w-4 h-4 text-gray-400" />
                  </button>
                ))}
                {mutuals.length === 0 && (
                  <p className="text-gray-500 text-center py-4 text-sm">No mutuals to share with</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-black rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Create Golf Event</h3>
              <button onClick={() => { setShowCreateModal(false); setCourseResults([]); setShowCourseDropdown(false); }} className="text-white hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Event Title *</label>
                <input
                  type="text"
                  required
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  placeholder="Sunday Morning Golf"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  rows="3"
                  placeholder="All skill levels welcome..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Golf Course *</label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={newEvent.course}
                    onChange={(e) => handleCourseSearch(e.target.value)}
                    onBlur={() => setTimeout(() => setShowCourseDropdown(false), 200)}
                    onFocus={() => { if (newEvent.course.length >= 2) setShowCourseDropdown(true); }}
                    className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                    placeholder="Search for a golf course..."
                    autoComplete="off"
                  />
                  {courseSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#22c55e] border-t-transparent rounded-full animate-spin" />}
                  {showCourseDropdown && courseResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {courseResults.map(course => (
                        <button key={course.id} type="button" onMouseDown={() => handleCourseSelect(course)} className="w-full text-left px-3 py-2 hover:bg-gray-800 border-b border-gray-700 last:border-0">
                          <p className="text-white text-sm font-medium">{course.clubName || course.courseName}</p>
                          <p className="text-gray-400 text-xs">{[course.city, course.state].filter(Boolean).join(', ')}{course.distance ? ` · ${course.distance.toFixed(1)} mi` : ''}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Address *</label>
                <GooglePlacesAutocomplete
                  value={newEvent.address}
                  onChange={(value) => setNewEvent({...newEvent, address: value})}
                  onSelect={(loc) => setNewEvent(prev => ({...prev, address: loc.displayName, addressLat: loc.lat, addressLon: loc.lon}))}
                  placeholder="Start typing the course address..."
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  required={true}
                />
                <p className="text-xs text-white mt-1">
                  Type the address and select from suggestions
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Date *</label>
                  <DateInput
                    value={newEvent.date}
                    onChange={(v) => setNewEvent({...newEvent, date: v})}
                    min={`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}`}
                    required
                    className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1">Time *</label>
                  <input
                    type="time"
                    required
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Max Players *</label>
                <input
                  type="number"
                  required
                  min="2"
                  max="8"
                  value={newEvent.maxPlayers}
                  onChange={(e) => setNewEvent({...newEvent, maxPlayers: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Visibility</label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setNewEvent({...newEvent, visibility: 'public'})}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${newEvent.visibility === 'public' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}
                  >
                    Public
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewEvent({...newEvent, visibility: 'private'})}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${newEvent.visibility === 'private' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}
                  >
                    <Lock className="w-4 h-4" />
                    <span>Private</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{newEvent.visibility === 'private' ? 'Only invited users can see and join this event' : 'Anyone can see and join this event'}</p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-2 py-1 border border-gray-700 text-white rounded-lg hover:bg-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateEvent}
                  className="flex-1 px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]"
                >
                  Create Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-black rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Edit Golf Event</h3>
              <button onClick={handleCancelEdit} className="text-white hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Event Title *</label>
                <input
                  type="text"
                  required
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  placeholder="Sunday Morning Golf"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Description</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  rows="3"
                  placeholder="All skill levels welcome..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Golf Course *</label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={newEvent.course}
                    onChange={(e) => handleCourseSearch(e.target.value)}
                    onBlur={() => setTimeout(() => setShowCourseDropdown(false), 200)}
                    onFocus={() => { if (newEvent.course.length >= 2) setShowCourseDropdown(true); }}
                    className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                    placeholder="Search for a golf course..."
                    autoComplete="off"
                  />
                  {courseSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#22c55e] border-t-transparent rounded-full animate-spin" />}
                  {showCourseDropdown && courseResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {courseResults.map(course => (
                        <button key={course.id} type="button" onMouseDown={() => handleCourseSelect(course)} className="w-full text-left px-3 py-2 hover:bg-gray-800 border-b border-gray-700 last:border-0">
                          <p className="text-white text-sm font-medium">{course.clubName || course.courseName}</p>
                          <p className="text-gray-400 text-xs">{[course.city, course.state].filter(Boolean).join(', ')}{course.distance ? ` · ${course.distance.toFixed(1)} mi` : ''}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Address *</label>
                <GooglePlacesAutocomplete
                  value={newEvent.address}
                  onChange={(value) => setNewEvent({...newEvent, address: value})}
                  onSelect={(loc) => setNewEvent(prev => ({...prev, address: loc.displayName, addressLat: loc.lat, addressLon: loc.lon}))}
                  placeholder="Start typing the course address..."
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  required={true}
                />
                <p className="text-xs text-white mt-1">
                  Type the address and select from suggestions
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Date *</label>
                  <DateInput
                    value={newEvent.date}
                    onChange={(v) => setNewEvent({...newEvent, date: v})}
                    min={`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}`}
                    required
                    className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1">Time *</label>
                  <input
                    type="time"
                    required
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Max Players *</label>
                <input
                  type="number"
                  required
                  min="2"
                  max="8"
                  value={newEvent.maxPlayers}
                  onChange={(e) => setNewEvent({...newEvent, maxPlayers: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:border-transparent text-white bg-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Visibility</label>
                <div className="flex space-x-2">
                  <button type="button" onClick={() => setNewEvent({...newEvent, visibility: 'public'})} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${newEvent.visibility === 'public' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}>Public</button>
                  <button type="button" onClick={() => setNewEvent({...newEvent, visibility: 'private'})} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${newEvent.visibility === 'private' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'}`}><Lock className="w-4 h-4" /><span>Private</span></button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{newEvent.visibility === 'private' ? 'Only invited users can see and join this event' : 'Anyone can see and join this event'}</p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex-1 px-2 py-1 border border-gray-700 text-white rounded-lg hover:bg-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className="flex-1 px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite to Event Modal */}
      {showInviteEvent && (() => {
        const event = events.find(e => e.id === showInviteEvent);
        if (!event) return null;
        const followList = users.filter(u => u.id !== currentUser.id && u.following?.includes(currentUser.id) && !event.attendees?.includes(u.id) && !event.invitedUsers?.includes(u.id));
        const userGroups = groups || [];
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-black border border-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Invite to Event</h3>
                <button onClick={() => { setShowInviteEvent(null); setSelectedInviteUsers([]); setSelectedInviteGroups([]); }} className="text-white hover:text-gray-300"><X className="w-6 h-6" /></button>
              </div>
              <div className="flex space-x-2 mb-4">
                <button onClick={() => setInviteTab('users')} className={`flex-1 py-2 rounded-lg text-sm font-medium ${inviteTab === 'users' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white'}`}>Followers</button>
                <button onClick={() => setInviteTab('groups')} className={`flex-1 py-2 rounded-lg text-sm font-medium ${inviteTab === 'groups' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white'}`}>My Groups</button>
              </div>
              {inviteTab === 'users' && (
                <div className="space-y-2 mb-4">
                  {followList.length === 0 ? <p className="text-gray-500 text-center py-4 text-sm">No users to invite</p> : followList.map(u => (
                    <button key={u.id} onClick={() => setSelectedInviteUsers(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])} className={`flex items-center justify-between w-full p-2 rounded-lg transition-colors ${selectedInviteUsers.includes(u.id) ? 'bg-[#16a34a] bg-opacity-20 border border-[#16a34a]' : 'hover:bg-gray-900 border border-transparent'}`}>
                      <div className="flex items-center space-x-3">
                        <img src={u.profilePicture} alt={u.name} className="w-10 h-10 rounded-full" />
                        <div className="text-left">
                          <p className="text-white font-medium text-sm">{u.name}</p>
                          <p className="text-xs text-gray-400">@{u.handle}</p>
                        </div>
                      </div>
                      {selectedInviteUsers.includes(u.id) && <CheckCircle className="w-5 h-5 text-[#16a34a]" />}
                    </button>
                  ))}
                </div>
              )}
              {inviteTab === 'groups' && (
                <div className="space-y-2 mb-4">
                  {userGroups.length === 0 ? <p className="text-gray-500 text-center py-4 text-sm">No groups to invite</p> : userGroups.map(g => (
                    <button key={g.id} onClick={() => setSelectedInviteGroups(prev => prev.includes(g.id) ? prev.filter(id => id !== g.id) : [...prev, g.id])} className={`flex items-center justify-between w-full p-2 rounded-lg transition-colors ${selectedInviteGroups.includes(g.id) ? 'bg-[#16a34a] bg-opacity-20 border border-[#16a34a]' : 'hover:bg-gray-900 border border-transparent'}`}>
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">{g.name}</p>
                        <p className="text-xs text-gray-400">{g.members?.length || 0} members</p>
                      </div>
                      {selectedInviteGroups.includes(g.id) && <CheckCircle className="w-5 h-5 text-[#16a34a]" />}
                    </button>
                  ))}
                </div>
              )}
              {(selectedInviteUsers.length > 0 || selectedInviteGroups.length > 0) && (
                <button onClick={() => handleInviteToEvent(showInviteEvent)} className="w-full py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]">
                  Send {selectedInviteUsers.length + selectedInviteGroups.length} Invite{selectedInviteUsers.length + selectedInviteGroups.length > 1 ? 's' : ''}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Game Setup Modal */}
      {showGameSetup && (
        <GameSetupModal event={showGameSetup} onClose={() => setShowGameSetup(null)} />
      )}
    </div>
  );
};

export default EventsView;
